import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../../api/client'
import type { Item, ProposalDetail } from '../../api/types'
import { Button, Field, Num } from '../../ui/primitives'

/**
 * Aggiunge alla proposta un articolo che il motore non ha suggerito.
 *
 * <p>Serve quando il negozio sa qualcosa che la previsione non sa: una richiesta del cliente, una
 * promozione locale, un vuoto sullo scaffale non ancora registrato dai dati.</p>
 *
 * <p>La ricerca chiede al server gli articoli ordinabili <b>con le regole dell'aggiunta
 * manuale</b>, che sono più strette di quelle dell'ordine speciale, e <b>del canale di questa
 * proposta</b>: un articolo che si ordina al fornitore non entra in una proposta CEDI e viceversa.
 * Così l'elenco contiene solo ciò che si può davvero aggiungere, e l'utente non scopre il rifiuto
 * dopo aver scelto.</p>
 */
export function AddLinePanel({
  proposalId,
  storeCode,
  supplierCode,
  isCedi,
  onAdded,
}: {
  proposalId: number
  storeCode: string
  supplierCode: string
  isCedi: boolean
  onAdded: (detail: ProposalDetail) => void
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<Item[]>()
  const [searching, setSearching] = useState(false)
  const [picked, setPicked] = useState<Item>()
  const [quantity, setQuantity] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const timer = useRef<number>(undefined)

  // Sul canale CEDI il fornitore della proposta è convenzionale e non filtra nulla: si cerca fra
  // tutti gli articoli rifornibili dal centro di distribuzione che serve il punto vendita.
  const search = useCallback(
    (text: string) => {
      window.clearTimeout(timer.current)
      if (text.trim().length < 2) {
        setResults(undefined)
        return
      }

      timer.current = window.setTimeout(() => {
        setSearching(true)
        setError(undefined)
        api
          .get<Item[]>('/items', {
            search: text.trim(),
            orderableInStore: storeCode,
            purpose: 'ManualAddition',
            supplierCode: isCedi ? undefined : supplierCode,
            // Il canale della proposta. Senza, l'elenco contiene anche articoli dell'altro canale,
            // che il server rifiuta al salvataggio: l'utente li vedrebbe, li sceglierebbe e si
            // prenderebbe l'errore dopo. Il filtro sta qui e non fra i risultati perché il server
            // ne restituisce venticinque, e scartarli dopo significherebbe mostrarne meno di
            // venticinque senza dirlo.
            channel: isCedi ? 'Cedi' : 'Supplier',
            take: 25,
          })
          .then(setResults)
          .catch((e: unknown) =>
            setError(e instanceof ApiError ? (e.detail ?? e.title) : 'Ricerca non riuscita.'),
          )
          .finally(() => setSearching(false))
      }, 300)
    },
    [storeCode, supplierCode, isCedi],
  )

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const reset = () => {
    setTerm('')
    setResults(undefined)
    setPicked(undefined)
    setQuantity('')
    setError(undefined)
  }

  const add = () => {
    const qty = Number(quantity)
    if (!picked || !Number.isFinite(qty) || qty <= 0) {
      setError('Indicare una quantità maggiore di zero.')
      return
    }

    setSaving(true)
    setError(undefined)
    api
      .post<ProposalDetail>(`/order-proposals/${proposalId}/lines`, {
        itemCode: picked.code,
        quantity: qty,
      })
      .then((detail) => {
        onAdded(detail)
        reset()
        setOpen(false)
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? (e.detail ?? e.title) : "Aggiunta non riuscita."),
      )
      .finally(() => setSaving(false))
  }

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        title="Aggiungi alla proposta un articolo che il motore non ha suggerito"
      >
        + Aggiungi articolo
      </Button>
    )
  }

  return (
    <div className="addline">
      <div className="addline-head">
        <strong>Aggiungi un articolo</strong>
        <Button
          variant="ghost"
          onClick={() => {
            reset()
            setOpen(false)
          }}
        >
          Chiudi
        </Button>
      </div>

      {picked ?
        <div className="addline-picked">
          <div>
            <div>{picked.description}</div>
            <small className="muted">
              {picked.code}
              {picked.supplierName ? ` · ${picked.supplierName}` : ''}
            </small>
          </div>
          <Field label="Pezzi">
            <input
              type="number"
              min={1}
              value={quantity}
              autoFocus
              onChange={(e) => setQuantity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
          </Field>
          <div className="addline-actions">
            <Button variant="primary" onClick={add} disabled={saving}>
              {saving ? 'Aggiunta…' : 'Aggiungi'}
            </Button>
            <Button variant="ghost" onClick={() => setPicked(undefined)} disabled={saving}>
              Cambia articolo
            </Button>
          </div>
          <div className="addline-note muted">
            La quantità viene arrotondata alla confezione: mai a zero, mai a mezze confezioni.
          </div>
        </div>
      : <>
          <Field label="Descrizione, codice interno o codice a barre" hint="Almeno due caratteri.">
            <input
              value={term}
              autoFocus
              placeholder="Cerca fra gli articoli ordinabili…"
              onChange={(e) => {
                setTerm(e.target.value)
                search(e.target.value)
              }}
            />
          </Field>

          {searching && <div className="muted">Ricerca in corso…</div>}

          {results && results.length === 0 && (
            <div className="muted">
              Nessun articolo ordinabile corrisponde. Un articolo può non comparire perché è fuori
              assortimento per questo punto vendita, non più in produzione, senza disponibilità al
              centro di distribuzione, o di una famiglia non ammessa in questo negozio.
            </div>
          )}

          {results && results.length > 0 && (
            <div className="addline-results">
              {results.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  className="addline-item"
                  onClick={() => {
                    setPicked(item)
                    setError(undefined)
                  }}
                >
                  <span className="addline-item-name">{item.description}</span>
                  <small className="muted">
                    {item.code}
                    {item.familyName ? ` · ${item.familyName}` : ''}
                  </small>
                </button>
              ))}
            </div>
          )}
        </>
      }

      {error && <div className="notice notice-error addline-error">{error}</div>}
    </div>
  )
}

/** Numero di pezzi già presenti, per la nota informativa. Tenuto qui per non duplicarlo. */
export function ManualLineCount({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="muted">
      {count === 1 ? '1 riga aggiunta a mano' : <><Num value={count} /> righe aggiunte a mano</>}
    </span>
  )
}
